/**
 * Product Image URLs
 *
 * Contains URLs for product images used in the estimator.
 * All images are now stored locally in src/images for better performance and reliability.
 */

export interface ProductImageSet {
  thumbnail: string;
  label: string;
  alt: string;
}

// ============================================================================
// UI ICONS (SVG) - Local Assets
// All UI icons are now stored locally in src/images/ui
// ============================================================================

export const LEGACY_UI_ICONS = {
  // Wall color icons
  wcbWhite: '/images/ui/wcb-white.svg',
  wcbGray: '/images/ui/wcb-gray.svg',

  // Orientation icons
  wcbVertical: '/images/ui/wcb-vertical.svg',
  wcbHorizontal: '/images/ui/wcb-horizontal.svg',

  // SlatWall coverage icons
  slatWall0Percent: '/images/ui/0-percent.svg',
  slatWall25Percent: '/images/ui/25-percent.svg',
  slatWall50Percent: '/images/ui/50-percent.svg',

  // SlatWall color icons
  swWhite: '/images/ui/sw-white.svg',
  swGray: '/images/ui/sw-gray.svg',

  // Ceiling icons (local SVGs with floor planes)
  wallsOnly: '/images/walls-only-no-ceiling.svg',
  wallsAndCeiling: '/images/walls-and-ceiling.svg',

  // Dimensions guide
  dimensionsGuide: '/images/ui/dimensions-guide.svg',

  // Garage icons
  garage: '/images/ui/garage.svg',
  noGarage: '/images/ui/no-garage.svg',
  garageSingle: '/images/ui/garage-single.svg',
  garageDouble: '/images/ui/garage-double.svg',
  garageGuide: '/images/ui/garage-guide.svg',
} as const;

// ============================================================================
// DIMENSION PREVIEW IMAGES (JPG) - Local Assets
// Application-specific dimension preview images stored in src/images/dimensions
// ============================================================================

export const DIMENSION_PREVIEW_IMAGES = {
  laundry: '/images/dimensions/laundry.jpg',
  garage: '/images/dimensions/garage.jpg',
  garage2: '/images/dimensions/garage2.jpg',
  basement: '/images/dimensions/basement.jpg',
  workshop: '/images/dimensions/workshop.jpg',
} as const;

// ============================================================================
// DESIGN PRESET IMAGES (JPG) - Local Assets
// Preview images for design presets stored in src/images/presets
// ============================================================================

export const LEGACY_DESIGN_PRESET_IMAGES = {
  'ultimate-modern': '/images/presets/ultimate-modern.jpg',
  'ultimate-classic': '/images/presets/ultimate-classic.jpg',
  'premium-classic': '/images/presets/premium-classic.jpg',
  'essential-modern': '/images/presets/essential-modern.jpg',
  'essential-classic': '/images/presets/essential-classic.jpg',
  'prefab-splash': '/images/presets/prefab-splash-img-1.jpg',
} as const;

// ============================================================================
// VISUALIZER IMAGES - Local Assets
// Visualizer images stored in src/images/visualizer
// ============================================================================

export const VISUALIZER_IMAGES = {
  base: '/images/visualizer/base.png',
  sample: '/images/visualizer/planner-estimate-sample-09-2024.jpg',
} as const;

// ============================================================================
// BRANDING IMAGES - Local Assets
// Branding images stored in src/images/branding
// ============================================================================

export const BRANDING_IMAGES = {
  logo: '/images/branding/trusscore-colour-dark-tagline.svg',
  logoLight: '/images/branding/trusscore-t-light.svg',
} as const;

// ============================================================================
// PRODUCT IMAGES (Current)
// ============================================================================

// Wall&CeilingBoard product images
export const WALL_PANEL_IMAGES: Record<'White' | 'Gray', ProductImageSet> = {
  White: {
    thumbnail: LEGACY_UI_ICONS.wcbWhite,
    label: 'White',
    alt: 'Trusscore Wall&CeilingBoard in White',
  },
  Gray: {
    thumbnail: LEGACY_UI_ICONS.wcbGray,
    label: 'Gray',
    alt: 'Trusscore Wall&CeilingBoard in Gray',
  },
};

// SlatWall product images
export const SLATWALL_IMAGES: Record<'White' | 'Gray', ProductImageSet> = {
  White: {
    thumbnail: LEGACY_UI_ICONS.swWhite,
    label: 'White',
    alt: 'Trusscore SlatWall in White',
  },
  Gray: {
    thumbnail: LEGACY_UI_ICONS.swGray,
    label: 'Gray',
    alt: 'Trusscore SlatWall in Gray',
  },
};

// Design preset preview images (using legacy images)
export const DESIGN_PRESET_IMAGES: Record<string, string> = LEGACY_DESIGN_PRESET_IMAGES;

// Space type images - not in legacy, keep empty for icon fallback
export const SPACE_TYPE_IMAGES: Record<string, string> = {
  garage: '',
  'living-space': '',
  basement: '',
  'laundry-room': '',
  other: '',
  shed: '',
  washroom: '',
  workshop: '',
};

export default {
  LEGACY_UI_ICONS,
  DIMENSION_PREVIEW_IMAGES,
  LEGACY_DESIGN_PRESET_IMAGES,
  VISUALIZER_IMAGES,
  BRANDING_IMAGES,
  WALL_PANEL_IMAGES,
  SLATWALL_IMAGES,
  DESIGN_PRESET_IMAGES,
  SPACE_TYPE_IMAGES,
};
